import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useCheckCoverageEligibilityMutation,
  useGetCoverageEligibilityHistoryQuery,
  useGetCoverageEligibilityQuery,
  useGetEncounterEligibilityQuery,
  useGetPatientCoveragesQuery,
  useGetPatientEligibilityQuery,
} from '@features/patient-coverages/store/api';
import type {
  EligibilityCheckRequest,
  EligibilityCurrentResponse,
} from '@features/patient-coverages/types';
import { useCan } from '@features/user';
import { Alert, Button, Skeleton, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { isEligibilityCheckDue } from '../utils/eligibilityCheckState';
import { eligibilityCoverageLabel } from '../utils/eligibilityCoverageLabel';
import { eligibilityPrecondition, eligibilityWriteError } from '../utils/manualEligibilityCommand';

import { CoverageBenefitsPanel } from './CoverageBenefitsPanel';
import { CoverageEligibilitySelector } from './CoverageEligibilitySelector';
import { CoverageManualSession } from './CoverageManualSession';
import { CoverageRecordSummary } from './CoverageRecordSummary';

interface Props {
  initialManualEntry?: boolean;
  patientId: string;
  encounterId?: string;
  canRunCheck: boolean;
  contextContent: (
    collectionAction: React.ReactNode,
    checkActions?: React.ReactNode,
  ) => React.ReactNode;
  collectionAction?: React.ReactNode;
}

export function CoverageEligibilityWorkspace({
  patientId,
  initialManualEntry = false,
  encounterId,
  canRunCheck,
  contextContent,
  collectionAction,
}: Props) {
  const { eligibilityRecord: tracking } = useEventTracking();
  const permission = useCan('manage_eligibility');
  const coverages = useGetPatientCoveragesQuery({ patientId });
  const patientContext = useGetPatientEligibilityQuery({ patientId }, { skip: !!encounterId });
  const encounterContext = useGetEncounterEligibilityQuery(
    { encounterId: encounterId ?? '' },
    { skip: !encounterId },
  );
  const context = encounterId ? encounterContext.currentData : patientContext.currentData;
  const contextPending = encounterId ? encounterContext.isLoading : patientContext.isLoading;
  const contextError = encounterId ? encounterContext.error : patientContext.error;
  const [selectionEncounter, setSelectionEncounter] = useState(encounterId);
  const encounterChanged = selectionEncounter !== encounterId;
  const latestEncounter = useRef({ id: encounterId, generation: 0 });
  if (latestEncounter.current.id !== encounterId) {
    latestEncounter.current = {
      id: encounterId,
      generation: latestEncounter.current.generation + 1,
    };
  }
  const openedInitial = useRef(false);
  const [selectedId, setSelectedId] = useState<string>();
  const [editing, setEditing] = useState<{
    initial: EligibilityCurrentResponse;
    label: string;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [checkStale, setCheckStale] = useState<EligibilityCurrentResponse | null>(null);
  const checkRequest = useRef<EligibilityCheckRequest | null>(null);
  const checkBasis = useRef<EligibilityCurrentResponse | null>(null);
  const [check, checkState] = useCheckCoverageEligibilityMutation();
  useEffect(() => {
    if (encounterChanged && !editing) {
      setSelectionEncounter(encounterId);
      setSelectedId(undefined);
      setNotice(null);
      setCheckStale(null);
      checkRequest.current = null;
      checkBasis.current = null;
      openedInitial.current = false;
    }
  }, [encounterChanged, encounterId, editing]);
  const policies = useMemo(
    () => coverages.currentData?.coverages.filter(coverage => coverage.kind === 'insurance') ?? [],
    [coverages.currentData],
  );
  useEffect(() => {
    if (
      selectedId === undefined &&
      policies.length &&
      !contextPending &&
      !contextError &&
      !!context &&
      !encounterChanged
    ) {
      setSelectedId(
        policies.find(
          coverage =>
            coverage.patient_insurance_coverage_id === context?.patient_insurance_coverage_id,
        )?.patient_insurance_coverage_id ?? policies[0].patient_insurance_coverage_id,
      );
    }
  }, [
    selectedId,
    policies,
    context?.patient_insurance_coverage_id,
    contextPending,
    context,
    contextError,
    encounterChanged,
  ]);
  const selected = policies.find(coverage => coverage.patient_insurance_coverage_id === selectedId);
  const args = { patientId, coverageId: selectedId ?? '' };
  const query = useGetCoverageEligibilityQuery(args, { skip: !selected });
  const history = useGetCoverageEligibilityHistoryQuery(args, { skip: !selected });
  const current = query.currentData;
  const recoveryMessage = encounterChanged
    ? 'The encounter changed. Your draft is preserved for its original coverage. Return to the original encounter or close this draft to switch coverage.'
    : coverages.error || contextError || query.error
      ? 'Coverage details could not be refreshed. Your draft is preserved; retry loading the details before saving.'
      : coverages.isLoading || contextPending || !context || query.isLoading
        ? 'Coverage details are loading. Your draft is preserved until they are available.'
        : context?.is_self_pay
          ? 'The current context is self-pay. Your insurance draft is preserved; restore the insurance context or close this draft.'
          : !selected
            ? 'This coverage is unavailable. Your draft is preserved.'
            : null;
  const canManage = permission && canRunCheck && !recoveryMessage;
  const canManual = canManage;
  const canCheck = canManage;
  useEffect(() => {
    if (initialManualEntry && !openedInitial.current && canManual && current && selected) {
      openedInitial.current = true;
      setEditing({ initial: current, label: eligibilityCoverageLabel(selected) });
    }
  }, [initialManualEntry, canManual, current, selected]);
  const sameContext = !!selectedId && context?.patient_insurance_coverage_id === selectedId;

  async function runCheck() {
    if (!current || !canCheck || checkState.isLoading || checkStale) return;
    tracking.automaticCheckClicked();
    const basis = checkBasis.current ?? current;
    const command = checkRequest.current ?? {
      ...eligibilityPrecondition(basis),
      idempotency_key: crypto.randomUUID(),
    };
    checkRequest.current = command;
    setNotice(null);
    const requestGeneration = latestEncounter.current.generation;
    try {
      await check({ ...args, eligibilityCheckRequest: command }).unwrap();
      if (latestEncounter.current.generation !== requestGeneration) return;
      checkRequest.current = null;
      checkBasis.current = null;
      setNotice('Automatic check recorded. Current benefits and source history are refreshed.');
    } catch (error) {
      if (latestEncounter.current.generation !== requestGeneration) return;
      const detail = eligibilityWriteError(error);
      if (
        detail &&
        'current' in detail &&
        detail.current.patient_id === patientId &&
        detail.current.patient_insurance_coverage_id === selectedId
      ) {
        setCheckStale(detail.current);
        setNotice(
          'The coverage changed before this check could be applied. Review current values before requesting another check.',
        );
      } else if (
        detail?.error === 'eligibility_check_failed' ||
        detail?.error === 'eligibility_check_outcome_uncertain'
      ) {
        checkRequest.current = null;
        checkBasis.current = null;
        setNotice(
          (detail.error === 'eligibility_check_outcome_uncertain'
            ? "The payer's response could not be confirmed."
            : 'The eligibility service could not complete this check.') +
            ' Saved benefits are unchanged. You can run a new automatic check.',
        );
      } else
        setNotice(
          detail?.error === 'eligibility_command_in_progress'
            ? 'This check is still processing. Retry to check the same command; a second payer request will not be started.'
            : 'The check could not be completed. Retry uses the same command.',
        );
    }
  }

  const historyStatus = history.error ? (
    <Alert severity="error">
      Could not load source history.{' '}
      <Button onClick={() => void history.refetch()}>Retry history</Button>
    </Alert>
  ) : !history.currentData ? (
    <Skeleton aria-label="Loading source history" />
  ) : undefined;

  const selector = (
    <CoverageEligibilitySelector
      coverages={policies}
      selectedCoverageId={selectedId ?? ''}
      disabled={!!editing || checkState.isLoading}
      onSelect={id => {
        setSelectedId(id);
        setNotice(null);
        setCheckStale(null);
        checkRequest.current = null;
        checkBasis.current = null;
      }}
    />
  );

  const checksInContext =
    sameContext &&
    context?.has_coverage &&
    (isEligibilityCheckDue(context) || context.readiness == null);
  const checkActions = current && selected && (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
      {canManual && (
        <Button
          variant="contained"
          disabled={checkState.isLoading || !!query.error}
          onClick={() =>
            setEditing({
              initial: current,
              label: eligibilityCoverageLabel(selected),
            })
          }
        >
          {current.state === 'empty' ? 'Manually enter benefits' : 'Manually update benefits'}
        </Button>
      )}
      {canCheck && (
        <Button
          variant="outlined"
          disabled={checkState.isLoading || !!editing || !!checkStale || !!query.error}
          onClick={() => void runCheck()}
        >
          {checkState.isLoading
            ? 'Checking…'
            : checkRequest.current
              ? 'Retry automatic check'
              : 'Run automatic check'}
        </Button>
      )}
    </Stack>
  );

  return (
    <Stack spacing={2.5}>
      {context?.is_self_pay ? (
        <Typography>
          This visit is self-pay. Insurance benefit verification does not apply.
        </Typography>
      ) : coverages.isLoading ? (
        <Skeleton aria-label="Loading coverages" height={80} />
      ) : coverages.error ? (
        <Alert severity="error">
          Could not load coverages. <Button onClick={() => void coverages.refetch()}>Retry</Button>
        </Alert>
      ) : (
        <>
          {(!selected || !current) && selector}
          {contextError && (
            <Alert severity="error">
              Could not load visit context.{' '}
              <Button
                onClick={() => void (encounterId ? encounterContext : patientContext).refetch()}
              >
                Retry context
              </Button>
            </Alert>
          )}
          {!context && !contextError && (
            <Typography role="status">Loading visit context…</Typography>
          )}
          {selectedId && !selected && (
            <Alert severity="warning">
              This coverage is no longer available. Choose a coverage to continue.
            </Alert>
          )}
          {selected && query.isLoading && (
            <Skeleton aria-label="Loading coverage benefits" height={160} />
          )}
          {selected && query.error && (
            <Alert severity="error">
              Could not refresh this coverage.{' '}
              <Button onClick={() => void query.refetch()}>Retry</Button>
            </Alert>
          )}
          {selected && current && (
            <>
              <CoverageBenefitsPanel
                key={current.patient_insurance_coverage_id}
                current={current}
                showHeading={false}
                coverageLabel={eligibilityCoverageLabel(selected)}
                selector={selector}
                contextContent={
                  sameContext &&
                  contextContent?.(
                    context?.has_coverage && collectionAction,
                    checksInContext ? checkActions : undefined,
                  )
                }
                historyItems={history.currentData?.items}
                historyStatus={historyStatus}
                actions={checksInContext ? undefined : checkActions}
              />
              {!canManual && (
                <Typography variant="body2" color="text.secondary">
                  Saved benefits remain available. Manual entry is currently unavailable.
                </Typography>
              )}
              {notice && (
                <Alert severity="info" role="status">
                  {notice}
                </Alert>
              )}
              {checkStale && (
                <Stack spacing={1}>
                  <CoverageRecordSummary current={checkStale} />
                  <Button
                    onClick={() => {
                      checkBasis.current = checkStale;
                      setCheckStale(null);
                      checkRequest.current = null;
                      setNotice('Latest values reviewed. You can request a new automatic check.');
                    }}
                  >
                    Use latest comparison
                  </Button>
                </Stack>
              )}
            </>
          )}
        </>
      )}
      {editing && (
        <CoverageManualSession
          key={editing.initial.patient_insurance_coverage_id}
          current={
            current?.patient_insurance_coverage_id === editing.initial.patient_insurance_coverage_id
              ? current
              : editing.initial
          }
          label={editing.label}
          recoveryMessage={recoveryMessage}
          onRetryDetails={
            coverages.error || contextError || query.error || !context
              ? () => {
                  if (coverages.error) void coverages.refetch();
                  if (contextError || !context)
                    void (encounterId ? encounterContext : patientContext).refetch();
                  if (query.error) void query.refetch();
                }
              : undefined
          }
          allowed={canManual && !!selected && !query.error}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setNotice('Benefits saved for this coverage.');
            void query.refetch();
            void history.refetch();
          }}
        />
      )}
    </Stack>
  );
}
